/** Друкована форма декларації єдинника (+ Додаток 1 ЄСВ, якщо подається) — ч.1113. */
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PERIOD_LABELS, singleTaxDeclarations } from '@/api/singleTaxDeclarations';
import { orgs } from '@/api/catalogs';
import {
  EsvAppendix, FopSectionI, FopSections, LegalSections, MpzAppendix, SingleTaxHeader, SingleTaxSignatures,
} from './SingleTaxBlank';

const GROUP: Record<string, number> = { FOP_1: 1, FOP_2: 2, FOP_3_3: 3, FOP_3_5: 3, SIMPLIFIED_3: 3 };

export default function SingleTaxDeclarationPrintPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const docQ = useQuery({ queryKey: ['single-tax-declaration', id], queryFn: () => singleTaxDeclarations.get(id!), enabled: !!id });
  const d = docQ.data;
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgListQ.data?.items.find((o) => o.id === d?.organization_id);
  if (!d) return <div className="card">Завантаження…</div>;
  const legal = d.form_code === 'J0103509';
  const esv = d.appendix_data?.esv;
  const withEsv = !legal && !!esv?.include && (esv.rows ?? []).some((r) => Number(r.amount) > 0);
  const mpz = d.appendix_data?.mpz;
  const withMpz = !!mpz?.include && (mpz.plots ?? []).length > 0;
  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4 portrait; margin: 1.2cm; }
          .print-page { page-break-before: always; }
          .print-page:first-of-type { page-break-before: auto; }
          nav, aside, header { display: none !important; }
          body { background: #fff !important; }
        }
      `}</style>
      <div className="no-print" style={{ display: 'flex', gap: '0.5rem', padding: '0.6rem 1rem', borderBottom: '1px solid #e2e8f0', alignItems: 'center' }}>
        <b>Друк: декларація єдинника — {PERIOD_LABELS[d.period_quarter]} {d.period_year}</b>
        <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
          {[withEsv ? 'Додаток 1 (ЄСВ)' : null, withMpz ? (legal ? 'Додаток (МПЗ)' : 'Додаток 2 (МПЗ)') : null]
            .filter(Boolean).map((x) => `+ ${x}`).join(' ') || 'без додатків'}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button className="primary" onClick={() => window.print()}>друкувати</button>
          <button onClick={() => navigate(`/taxation/single-tax-declarations/${id}`)}>до декларації</button>
        </div>
      </div>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '1rem' }}>
        <div className="print-page">
          <SingleTaxHeader doc={d} org={org} fields={d.fields ?? {}} readOnly print />
          {legal ? <LegalSections values={d.values ?? {}} readOnly print /> : (
            <>
              <FopSectionI fields={d.fields ?? {}} readOnly print />
              <FopSections values={d.values ?? {}} fields={d.fields ?? {}} readOnly print group={org ? (GROUP[org.tax_system] ?? null) : null} />
            </>
          )}
          <SingleTaxSignatures doc={d} org={org} print />
        </div>
        {withEsv && esv && (
          <div className="print-page" style={{ marginTop: '1.5rem' }}>
            <EsvAppendix doc={d} org={org} esv={esv} readOnly print />
          </div>
        )}
        {withMpz && mpz && (
          <div className="print-page" style={{ marginTop: '1.5rem' }}>
            <MpzAppendix doc={d} legal={legal} mpz={mpz} readOnly print />
          </div>
        )}
      </div>
    </div>
  );
}
